import { useCallback, useMemo, useState } from "react";
import { FileText, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { KnowledgeDrawer } from "@/components/agent/drawers/KnowledgeDrawer";
import DocumentDetailsDrawer from "@/components/knowledge-base/details-drawer/DocumentDetailsDrawer";
import { GenieDocumentCard } from "@/components/knowledge-base/GenieDocumentCard";
import { useDocuments } from "@/hooks/documents/useDocumentFetch";
import {
  useDeleteDocument,
  useRemoveDocumentFromKB,
  useSendDocumentToElevenLabs,
  useUpdateDocument,
} from "@/hooks/documents/useDocumentMutations";
import { useKnowledgeBaseToggle } from "@/hooks/documents/useKnowledgeBaseToggle";
import { DocumentService } from "@/services/api/DocumentService";
import { Agent } from "@/types/agent/agent";
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

interface KnowledgeCardProps {
  agent: Agent;
  brand?: string;
}

export const KnowledgeCard = ({ agent, brand = "#4E9CFF" }: KnowledgeCardProps) => {
  const [selectedDocument, setSelectedDocument] = useState<DocumentWithMetadata | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const hasKnowledgeBase = (agent.knowledge_base?.length ?? 0) > 0;
  const { data: fetchedDocuments = [] } = useDocuments("my-documents", { agentId: agent.id }, undefined, !hasKnowledgeBase);

  const knowledgeBaseDocuments = useMemo<DocumentWithMetadata[]>(
    () => hasKnowledgeBase ? (agent.knowledge_base as DocumentWithMetadata[]) : fetchedDocuments,
    [agent.knowledge_base, fetchedDocuments, hasKnowledgeBase],
  );

  const deleteDocumentMutation = useDeleteDocument();
  const updateDocumentMutation = useUpdateDocument();
  const sendToElevenLabsMutation = useSendDocumentToElevenLabs();
  const removeFromKBMutation = useRemoveDocumentFromKB();

  const handleUpdateDocument = async (document: DocumentWithMetadata, updates: Partial<DocumentWithMetadata>) => {
    const updatedDoc = await updateDocumentMutation.mutateAsync({ document, updates });
    if (selectedDocument?.id === document.id && updatedDoc) setSelectedDocument(updatedDoc);
  };

  const handleDeleteDocument = async (id: string) => {
    deleteDocumentMutation.mutate(id);
    setSelectedDocument(null);
  };

  const handleViewDocument = async (document: DocumentWithMetadata) => {
    const pathToUse = document.source_path || document.parsed_path;
    if (pathToUse) {
      const signedUrl = await DocumentService.getSignedUrl(pathToUse);
      if (signedUrl) window.open(signedUrl, "_blank");
    }
  };

  const { toggleDocuments } = useKnowledgeBaseToggle();

  const handleRemoveFromKB = useCallback(
    async (doc: DocumentWithMetadata) => {
      await removeFromKBMutation(doc);
      // Sync genie settings so selectedDocuments stays accurate
      await toggleDocuments(agent, doc, false);
    },
    [agent, removeFromKBMutation, toggleDocuments],
  );

  return (
    <>
      <OverviewSectionCard
        title="Knowledge Base"
        subtitle="Documents linked to this genie's knowledge base"
        genieIcon="book-open"
        brand={brand}
        secondaryColor={agent.branding?.secondaryColor}
        gradientEnabled={agent.branding?.gradientEnabled}
        gradientAngle={agent.branding?.gradientAngle}
      >
        <div className="space-y-4">
          {knowledgeBaseDocuments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium text-gray-900 mb-2">No documents linked yet</p>
              <p className="text-sm text-gray-600">Link existing documents below</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {knowledgeBaseDocuments.map((doc) => (
                <GenieDocumentCard
                  key={doc.id}
                  document={doc}
                  onClick={() => setSelectedDocument(doc)}
                  onView={() => handleViewDocument(doc)}
                  onEdit={() => setSelectedDocument(doc)}
                  onDetach={() => handleRemoveFromKB(doc)}
                  showActions={true}
                  genieUserId={agent.user_id}
                />
              ))}
            </div>
          )}

          <Button variant="outline" size="sm" onClick={() => setDrawerOpen(true)} className="shrink-0 gap-1.5 ml-auto flex">
            <Plus className="w-3.5 h-3.5" />
            Manage Knowledge
          </Button>
        </div>
      </OverviewSectionCard>

      <KnowledgeDrawer
        genie={agent}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />

      <DocumentDetailsDrawer
        document={selectedDocument}
        isOpen={!!selectedDocument}
        onClose={() => setSelectedDocument(null)}
        onSendToKB={sendToElevenLabsMutation}
        onRemoveFromKB={removeFromKBMutation}
        onDocumentUpdate={handleUpdateDocument}
        onDocumentDelete={handleDeleteDocument}
      />
    </>
  );
};
